// src/pages/workflow/CredentialListPage.tsx
// Manage workflow credentials (metadata only — secrets stored in SecretService).

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Badge,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { FormDialog, TitleInput, AutoGrowTextarea } from '@/components/form';
import type { CreateCredentialRequest } from '@core/types/workflow';
import {
  credentialTypeMap,
  credentialTypeOptions,
  getEntityId,
  formatRelativeTime,
  validateCredentialForm,
} from '@/features/workflow/logic';

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function KeyIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export const CredentialListPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();

  const [credentials, setCredentials] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Create form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    credential_type: 'CREDENTIAL_TYPE_API_KEY',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setIsLoading(true);
    try {
      await store.ListCredentials?.();
      setCredentials(store.items || []);
    } catch {
      // Error handled by store
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let items = credentials;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (c: any) =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.description || '').toLowerCase().includes(q),
      );
    }
    if (typeFilter !== 'all') {
      items = items.filter((c: any) => c.credential_type === typeFilter);
    }
    return items;
  }, [credentials, search, typeFilter]);

  const handleCreate = async () => {
    const errors = validateCredentialForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      await store.CreateCredential?.({
        name: formData.name,
        description: formData.description,
        credential_type: formData.credential_type as CreateCredentialRequest['credential_type'],
      });
      setShowCreateModal(false);
      setFormData({ name: '', description: '', credential_type: 'CREDENTIAL_TYPE_API_KEY' });
      loadCredentials();
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (credId: string) => {
    if (!confirm(t('workflow.deleteCredentialConfirm'))) return;
    await store.DeleteCredential?.(credId);
    loadCredentials();
  };

  const handleTest = async (credId: string) => {
    await store.TestCredential?.(credId, {});
    loadCredentials();
  };

  // Edit — GetCredential loads the full record, UpdateCredential saves it.
  const openEdit = async (credId: string) => {
    setEditingId(credId);
    setIsLoadingEdit(true);
    try {
      const full = await store.GetCredential?.(credId) as any;
      const cred = full?.credential ?? full;
      setEditForm({ name: cred?.name ?? '', description: cred?.description ?? '' });
    } catch {
      // Error handled by store
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setIsSavingEdit(true);
    try {
      const existing = credentials.find((c: any) => getEntityId(c) === editingId) ?? {};
      await store.UpdateCredential?.(editingId, {
        credential: { ...existing, name: editForm.name.trim(), description: editForm.description },
      } as any);
      setEditingId(null);
      loadCredentials();
    } catch {
      // Error handled by store
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm">
            <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('workflow.credentials')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('workflow.credentials')}</h1>
        </div>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="h-4 w-4 mr-1.5" />
          {t('workflow.addCredential')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Input
          placeholder={t('workflow.searchCredentials')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">{t('workflow.allTypes')}</option>
          {credentialTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </Select>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Credential grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="animate-pulse space-y-3">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <KeyIcon
              path="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4"
            />
            <h3 className="text-lg font-medium text-foreground mb-1">{t('workflow.noCredentials')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('workflow.noCredentialsDesc')}</p>
            <Button onClick={() => setShowCreateModal(true)}>{t('workflow.addCredential')}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((cred: any) => {
            const credId = getEntityId(cred);
            const typeInfo = credentialTypeMap[cred.credential_type] || credentialTypeMap.CREDENTIAL_TYPE_API_KEY;
            return (
              <Card key={credId} className="group">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <KeyIcon path={typeInfo.icon} className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => openEdit(credId)}
                        className="font-semibold text-foreground truncate hover:underline text-left"
                      >
                        {cred.name}
                      </button>
                      <p className="text-xs text-muted-foreground">{t(typeInfo.labelKey)}</p>
                    </div>
                    {cred.is_verified ? (
                      <Badge variant="success" size="sm">
                        <CheckIcon className="h-3 w-3 mr-1" />{t('workflow.verified')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" size="sm">{t('workflow.unverified')}</Badge>
                    )}
                  </div>

                  {cred.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{cred.description}</p>
                  )}

                  {cred.field_keys?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {cred.field_keys.map((key: string) => (
                        <span key={key} className="px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground font-mono">
                          {key}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(cred.created_at)}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" onClick={() => handleTest(credId)}>
                        {t('workflow.test')}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(credId)}>
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <FormDialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
        submitLabel={t('workflow.addCredential')}
        canSubmit={!!formData.name.trim()}
        isSubmitting={isSubmitting}
        dirty={!!formData.name.trim() || !!formData.description.trim()}
        aria-label={t('workflow.addCredential')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <TitleInput
            value={formData.name}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, name: value }));
              if (formErrors.name) setFormErrors((prev) => { const n = { ...prev }; delete n.name; return n; });
            }}
            placeholder={t('workflow.field.name')}
            initialFocus
            onEnter={() => document.getElementById('credential-create-description')?.focus()}
            aria-label={t('workflow.field.name')}
          />
          {formErrors.name && <p className="text-sm text-destructive">{t(formErrors.name)}</p>}
          <AutoGrowTextarea
            id="credential-create-description"
            value={formData.description}
            onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
            placeholder={t('workflow.field.description')}
            minRows={1}
          />
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">{t('workflow.credentialType')}</label>
            <Select
              value={formData.credential_type}
              onChange={(e) => setFormData((prev) => ({ ...prev, credential_type: e.target.value }))}>
              {credentialTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </Select>
            {formErrors.credential_type && (
              <p className="text-sm text-destructive mt-1">{t(formErrors.credential_type)}</p>
            )}
          </div>
        </div>
      </FormDialog>

      {/* Edit dialog — GetCredential loads, UpdateCredential saves */}
      <FormDialog
        open={!!editingId}
        onClose={() => setEditingId(null)}
        onSubmit={saveEdit}
        submitLabel={t('common.save')}
        canSubmit={!isLoadingEdit && !!editForm.name.trim()}
        isSubmitting={isSavingEdit}
        dirty={!!editForm.name.trim() || !!editForm.description.trim()}
        aria-label={t('workflow.editCredential')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          {isLoadingEdit ? (
            <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />
          ) : (
            <>
              <TitleInput
                value={editForm.name}
                onChange={(value) => setEditForm((p) => ({ ...p, name: value }))}
                placeholder={t('workflow.field.name')}
                initialFocus
                onEnter={() => document.getElementById('credential-edit-description')?.focus()}
                aria-label={t('workflow.field.name')}
              />
              <AutoGrowTextarea
                id="credential-edit-description"
                value={editForm.description}
                onChange={(value) => setEditForm((p) => ({ ...p, description: value }))}
                placeholder={t('workflow.field.description')}
                minRows={1}
              />
            </>
          )}
        </div>
      </FormDialog>
    </div>
  );
});

export default CredentialListPage;
